import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Copy, Loader2, CheckCircle, XCircle } from 'lucide-react';
import api, { testExchangeConnection } from '../services/apiClient';
import './ExchangeConfigModal.css';

// Exchange-specific field configurations
const EXCHANGE_FIELDS = {
  binance: {
    label: 'Binance',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'text', required: true },
      { key: 'apiSecret', label: 'API Secret', type: 'password', required: true },
    ],
  },
  bybit: {
    label: 'Bybit',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'text', required: true },
      { key: 'apiSecret', label: 'API Secret', type: 'password', required: true },
    ],
  },
  okx: {
    label: 'OKX',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'text', required: true },
      { key: 'apiSecret', label: 'API Secret', type: 'password', required: true },
      { key: 'apiPassphrase', label: 'Passphrase', type: 'password', required: true },
    ],
  },
  kucoin: {
    label: 'KuCoin',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'text', required: true },
      { key: 'apiSecret', label: 'API Secret', type: 'password', required: true },
      { key: 'apiPassphrase', label: 'Passphrase', type: 'password', required: true },
    ],
  },
  gateio: {
    label: 'Gate.io',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'text', required: true },
      { key: 'apiSecret', label: 'API Secret', type: 'password', required: true },
    ],
  },
  woox: {
    label: 'WOO X',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'text', required: true },
      { key: 'apiSecret', label: 'API Secret', type: 'password', required: true },
      { key: 'apiPassphrase', label: 'Application ID', type: 'text', required: false },
    ],
  },
  hyperliquid: {
    label: 'Hyperliquid',
    fields: [
      { key: 'apiKey', label: 'API Key (Agent Wallet Address)', type: 'text', required: true, placeholder: '0x... (agent wallet address created by Hyperliquid)' },
      { key: 'secretKey', label: 'Private Key (Secret Key)', type: 'password', required: true },
      { key: 'apiWalletAddress', label: 'Main Wallet Address', type: 'text', required: true, placeholder: '0x... (wallet that signed API creation)' },
      { key: 'mainWalletPrivateKey', label: 'Main Wallet Private Key', type: 'password', required: false, placeholder: '0x... (private key of main wallet - required for deposits)' },
    ],
  },
  bullish: {
    label: 'Bullish',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'text', required: true },
      { key: 'apiSecret', label: 'API Secret', type: 'password', required: true },
      { key: 'accountIdExchange', label: 'Account ID', type: 'text', required: false },
    ],
  },
  lighter: {
    label: 'Lighter',
    fields: [
      { key: 'apiKeyIndex', label: 'API Key Index (3-254)', type: 'text', required: true },
      { key: 'accountIndex', label: 'Account Index', type: 'text', required: true },
      { key: 'publicKey', label: 'Public Key', type: 'text', required: true },
      { key: 'privateKey', label: 'Private Key', type: 'password', required: true },
      { key: 'baseUrl', label: 'Base URL (optional)', type: 'text', required: false },
    ],
  },
  extended: {
    label: 'Extended',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'text', required: true },
      { key: 'apiSecret', label: 'Stark Private Key', type: 'password', required: true },
      { key: 'clientId', label: 'Client ID', type: 'text', required: false },
      { key: 'starkPublicKey', label: 'Stark Public Key', type: 'text', required: true },
      { key: 'vaultNo', label: 'Vault No', type: 'text', required: true },
    ],
  },
};

// Chain options for deposit addresses
const CHAIN_OPTIONS = [
  { id: 'arbitrum', label: 'Arbitrum' },
  { id: 'ethereum', label: 'Ethereum' },
];

const STABLECOINS = ['USDT', 'USDC'];

// Helper to shorten address
const shortenAddress = (addr, start = 6, end = 4) => {
  if (!addr || addr.length < start + end + 3) return addr;
  return `${addr.slice(0, start)}...${addr.slice(-end)}`;
};

function ExchangeConfigModal({ exchange, exchangeData, onClose, onSave }) {
  const config = EXCHANGE_FIELDS[exchange] || { label: exchange, fields: [] };
  
  // Form state for credentials
  const [credentials, setCredentials] = useState({});
  const [acceptedStablecoins, setAcceptedStablecoins] = useState([]);
  const [depositAddresses, setDepositAddresses] = useState({});
  const [newAddress, setNewAddress] = useState({ chain: '', address: '' });
  
  // Whitelisting settings
  const [withdrawalEnabled, setWithdrawalEnabled] = useState(true);
  const [transferLimitEnabled, setTransferLimitEnabled] = useState(false);
  const [transferLimit, setTransferLimit] = useState('');
  
  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);

  // Initialize form with existing data - only once when modal opens
  // Use a ref to track if we've already initialized to prevent resetting form on parent re-renders
  const initializedRef = useRef(false);
  const lastExchangeRef = useRef(exchange);
  
  // Reset and reinitialize when exchange changes
  useEffect(() => {
    // If exchange changed, reset everything
    if (lastExchangeRef.current !== exchange) {
      initializedRef.current = false;
      setCredentials({});
      setDepositAddresses({});
      setNewAddress({ chain: '', address: '' });
      setWithdrawalEnabled(true);
      setTransferLimitEnabled(false);
      setTransferLimit('');
      setAcceptedStablecoins([]);
      setError(null);
      setTestResult(null);
      lastExchangeRef.current = exchange;
    }
    
    // Initialize form data only once per exchange
    if (exchangeData && !initializedRef.current) {
      const existingAddresses = exchangeData.depositAddresses || {};
      setDepositAddresses(existingAddresses);
      setWithdrawalEnabled(exchangeData.withdrawalEnabled !== false);
      setTransferLimit(exchangeData.transferLimit || '');
      setTransferLimitEnabled(!!exchangeData.transferLimit);
      const stablecoins = exchangeData?.acceptedStablecoins || exchangeData?.extraInfo?.accepted_stablecoins || [];
      setAcceptedStablecoins(stablecoins.map((s) => s.toUpperCase()));
      const mainWallet = exchangeData?.apiWalletAddress || exchangeData?.extraInfo?.api_wallet_address || "";
      if (mainWallet) {
        setCredentials((prev) => ({
          ...prev,
          apiWalletAddress: prev.apiWalletAddress || mainWallet,
        }));
      }
      
      // Set default chain to first available (not already used)
      const availableChains = CHAIN_OPTIONS.filter(c => !existingAddresses[c.id]);
      if (availableChains.length > 0) {
        setNewAddress({ chain: availableChains[0].id, address: '' });
      }
      
      initializedRef.current = true;
    }
    // Credentials are not loaded for security - user must re-enter
  }, [exchange, exchangeData]);

  const handleCredentialChange = (key, value) => {
    setCredentials(prev => ({ ...prev, [key]: value }));
  };

  const handleStablecoinToggle = (asset) => {
    setAcceptedStablecoins((prev) => {
      if (prev.includes(asset)) {
        return prev.filter((a) => a !== asset);
      }
      return [...prev, asset];
    });
  };

  const handleAddAddress = () => {
    if (!newAddress.address.trim() || !newAddress.chain) return;
    const updatedAddresses = {
      ...depositAddresses,
      [newAddress.chain]: newAddress.address.trim()
    };
    setDepositAddresses(updatedAddresses);
    
    // Reset to next available chain
    const availableChains = CHAIN_OPTIONS.filter(c => !updatedAddresses[c.id]);
    setNewAddress({ 
      chain: availableChains.length > 0 ? availableChains[0].id : '', 
      address: '' 
    });
  };

  const handleRemoveAddress = (chain) => {
    setDepositAddresses(prev => {
      const updated = { ...prev };
      delete updated[chain];
      return updated;
    });
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      await testExchangeConnection(exchange);
      setTestResult({ success: true, message: 'Connection successful!' });
    } catch (err) {
      setTestResult({ 
        success: false, 
        message: err.response?.data?.error || err.message || 'Connection failed' 
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    console.log('[DEBUG] handleSave called!');
    console.log('[DEBUG] depositAddresses at start:', depositAddresses);
    console.log('[DEBUG] newAddress at start:', newAddress);
    setIsSaving(true);
    setError(null);
    
    try {
      // 1. Save credentials only when required fields are provided
      let hasCredentials = false;
      if (exchange === 'hyperliquid') {
        hasCredentials = !!(credentials.apiKey && credentials.secretKey);
      } else if (exchange === 'lighter') {
        hasCredentials = !!(credentials.apiKeyIndex && credentials.accountIndex && credentials.publicKey && credentials.privateKey);
      } else if (exchange === 'extended') {
        hasCredentials = !!(credentials.apiKey && (credentials.apiSecret || credentials.starkKeyPrivate));
      } else {
        hasCredentials = !!(credentials.apiKey && (credentials.apiSecret || credentials.secretKey));
      }
      if (hasCredentials) {
        // Build credentials payload for the new schema
        let apiKey = '';
        let apiSecret = '';
        
        // Exchange-specific field mappings
        if (exchange === 'hyperliquid') {
          // For Hyperliquid:
          // - apiKey: the agent wallet address (API key created by Hyperliquid)
          // - secretKey: the private key of the agent wallet
          apiKey = credentials.apiKey || '';
          apiSecret = credentials.secretKey || '';
        } else if (exchange === 'lighter') {
          // For Lighter:
          // - apiKey: API Key Index (3-254) - stored as apiKey
          // - apiSecret: Private Key - stored as apiSecret
          // - extraInfo.account_index: Account Index
          // - extraInfo.public_key: Public Key
          apiKey = credentials.apiKeyIndex || '';
          apiSecret = credentials.privateKey || '';
        } else if (exchange === 'extended') {
          // For Extended:
          // - apiKey: apiKey
          // - apiSecret: starkKeyPrivate (labeled as "Stark Private Key" in UI)
          apiKey = credentials.apiKey || '';
          apiSecret = credentials.apiSecret || credentials.starkKeyPrivate || '';
        } else {
          // For other exchanges, use standard mapping
          apiKey = credentials.apiKey || credentials.walletAddress || credentials.apiWalletAddress || '';
          apiSecret = credentials.apiSecret || credentials.secretKey || '';
        }
        
        const payload = {
          apiKey,
          apiSecret,
          passphrase: credentials.apiPassphrase || undefined,
          label: credentials.accountIdExchange || undefined,
          isEnabled: true,
        };
        
        // Build extraInfo for exchanges that need it
        const extraInfo = {};
        
        // For Hyperliquid, add apiWalletAddress and mainWalletPrivateKey to extraInfo
        if (exchange === 'hyperliquid') {
          if (credentials.apiWalletAddress) {
            extraInfo.api_wallet_address = credentials.apiWalletAddress;
            // Also send apiWalletAddress for backward compatibility with API
            payload.apiWalletAddress = credentials.apiWalletAddress;
          }
          // Main wallet private key is required for deposits (funds credited to msg.sender)
          if (credentials.mainWalletPrivateKey) {
            extraInfo.main_wallet_private_key = credentials.mainWalletPrivateKey;
          }
        }
        
        // For Lighter, add Account_Index and Public_Key to extraInfo
        // Note: API Key Index is stored as apiKey, not in extraInfo
        if (exchange === 'lighter') {
          if (credentials.accountIndex) {
            extraInfo.account_index = credentials.accountIndex;
          }
          if (credentials.publicKey) {
            extraInfo.public_key = credentials.publicKey;
          }
          // Optional: base URL (defaults to mainnet)
          if (credentials.baseUrl) {
            extraInfo.base_url = credentials.baseUrl;
          }
        }
        
        // For Extended, add Stark_Public_Key and Vault_No to extraInfo
        if (exchange === 'extended') {
          if (credentials.starkPublicKey) {
            extraInfo.stark_public_key = credentials.starkPublicKey;
          }
          if (credentials.vaultNo) {
            extraInfo.vault_no = credentials.vaultNo;
          }
        }

        if (acceptedStablecoins.length > 0) {
          extraInfo.accepted_stablecoins = acceptedStablecoins;
        }
        
        // Add extraInfo to payload if it has any content
        if (Object.keys(extraInfo).length > 0) {
          payload.extraInfo = extraInfo;
        }
        
        await api.put(`/admin/exchanges/${exchange}/credentials`, payload);
      } else if (acceptedStablecoins.length > 0 || credentials.apiWalletAddress || credentials.mainWalletPrivateKey) {
        const extraInfo = {};
        if (exchange === 'hyperliquid') {
          if (credentials.apiWalletAddress) {
            extraInfo.api_wallet_address = credentials.apiWalletAddress;
          }
          if (credentials.mainWalletPrivateKey) {
            extraInfo.main_wallet_private_key = credentials.mainWalletPrivateKey;
          }
        }
        if (acceptedStablecoins.length > 0) {
          extraInfo.accepted_stablecoins = acceptedStablecoins;
        }
        await api.put(`/admin/exchanges/${exchange}/metadata`, { extraInfo });
      }

      // 2. Save deposit addresses - each one is a separate whitelisted wallet
      const finalAddresses = { ...depositAddresses };
      if (newAddress.address && newAddress.address.trim()) {
        finalAddresses[newAddress.chain] = newAddress.address.trim();
      }
      
      console.log('[DEBUG] Saving deposit addresses:', finalAddresses);
      console.log('[DEBUG] Current depositAddresses state:', depositAddresses);
      console.log('[DEBUG] newAddress:', newAddress);
      
      // Save each deposit address with whitelisting settings
      for (const [chain, address] of Object.entries(finalAddresses)) {
        console.log(`[DEBUG] Saving ${chain}: ${address}`);
        const response = await api.post('/admin/whitelisted-wallets', {
          address,
          chain,
          exchangeId: exchange,
          walletType: 'deposit',
          label: `${exchange} ${chain} deposit`,
          withdrawalEnabled: withdrawalEnabled,
          transferLimit: transferLimitEnabled && transferLimit ? parseFloat(transferLimit) : 0,
        });
        console.log(`[DEBUG] Response for ${chain}:`, response.data);
      }

      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to remove ${config.label} configuration?`)) {
      return;
    }
    
    setIsSaving(true);
    setError(null);
    
    try {
      // Delete credentials
      await api.delete(`/admin/exchanges/${exchange}/credentials`);
      
      // Note: deposit addresses remain - they might need manual cleanup
      // through the whitelisted wallets management
      
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="exchange-config-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>{config.label}</h2>
          <button className="btn-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Error display */}
        {error && (
          <div className="modal-error">
            <XCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className="modal-content">
          {/* API Credentials Section */}
          <section className="config-section">
            <h3>API Credentials</h3>
            {exchangeData?.hasApiKey && (
              <p className="section-hint">Configured. Enter new values to update.</p>
            )}
            
            <div className="credentials-form">
              {config.fields.map(field => {
                // Determine the placeholder - show preview for API key if available
                let placeholder = field.placeholder || `Enter ${field.label.toLowerCase()}`;
                let hasPreview = false;
                
                if (field.key === 'apiKey' && exchangeData?.apiKeyPreview) {
                  // For all exchanges, show API key preview
                  placeholder = `Current: ${exchangeData.apiKeyPreview}`;
                  hasPreview = true;
                } else if (exchange === 'lighter' && field.key === 'apiKeyIndex' && exchangeData?.apiKeyPreview) {
                  // Lighter stores API Key Index in apiKey; show same preview in API Key Index field
                  placeholder = `Current: ${exchangeData.apiKeyPreview}`;
                  hasPreview = true;
                } else if (exchange === 'lighter' && field.key === 'publicKey' && exchangeData?.extraInfo?.public_key) {
                  // Lighter: show masked public key from extraInfo (not decrypted secret)
                  const pk = exchangeData.extraInfo.public_key;
                  const preview = pk.length > 12 ? `${pk.slice(0, 6)}...${pk.slice(-4)}` : pk;
                  placeholder = `Current: ${preview}`;
                  hasPreview = true;
                } else if (field.key === 'walletAddress' && exchangeData?.apiKeyPreview) {
                  placeholder = `Current: ${exchangeData.apiKeyPreview}`;
                  hasPreview = true;
                } else if (field.key === 'apiWalletAddress' && exchangeData?.apiWalletAddress) {
                  // For Hyperliquid, show the main wallet address preview
                  const addr = exchangeData.apiWalletAddress;
                  const preview = addr.length > 12 
                    ? `${addr.slice(0, 6)}...${addr.slice(-4)}`
                    : addr;
                  placeholder = `Current: ${preview}`;
                  hasPreview = true;
                }
                
                return (
                  <div key={field.key} className="form-field">
                    <label>
                      {field.label}
                      {field.required && <span className="required">*</span>}
                    </label>
                    <input
                      type={field.type}
                      value={credentials[field.key] || ''}
                      onChange={(e) => handleCredentialChange(field.key, e.target.value)}
                      placeholder={placeholder}
                      className={hasPreview ? 'has-preview' : ''}
                    />
                  </div>
                );
              })}
            </div>
          </section>

          {/* Deposit Addresses Section */}
          <section className="config-section">
            <h3>Deposit Addresses</h3>
            
            {/* Existing addresses */}
            <div className="addresses-list">
              {Object.entries(depositAddresses).map(([chain, addr]) => (
                <div key={chain} className="address-row">
                  <span className="chain-badge">{chain.toUpperCase()}</span>
                  <span className="address-value" title={addr}>
                    {shortenAddress(addr, 10, 8)}
                  </span>
                  <button 
                    className="btn-icon" 
                    onClick={() => copyToClipboard(addr)}
                    title="Copy address"
                  >
                    <Copy size={14} />
                  </button>
                  <button 
                    className="btn-icon btn-remove" 
                    onClick={() => handleRemoveAddress(chain)}
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            
            {/* Add new address */}
            <div className="add-address-form">
              <select 
                value={newAddress.chain}
                onChange={(e) => setNewAddress({ ...newAddress, chain: e.target.value })}
              >
                {CHAIN_OPTIONS
                  .filter(c => !depositAddresses[c.id])
                  .map(c => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
              </select>
              <input
                type="text"
                value={newAddress.address}
                onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                placeholder="Enter deposit address (0x...)"
              />
              <button 
                className="btn-add"
                onClick={handleAddAddress}
                disabled={!newAddress.address.trim()}
              >
                <Plus size={16} />
              </button>
            </div>
          </section>

          {/* Accepted Stablecoins */}
          <section className="config-section">
            <h3>Accepted Stablecoins</h3>
            <div className="stablecoin-options">
              {STABLECOINS.map((asset) => (
                <label key={asset} className="stablecoin-option">
                  <input
                    type="checkbox"
                    checked={acceptedStablecoins.includes(asset)}
                    onChange={() => handleStablecoinToggle(asset)}
                  />
                  <span>{asset}</span>
                </label>
              ))}
            </div>
            <div className="helper-text">
              Used by the backend to auto-swap assets before deposit.
            </div>
          </section>

          {/* Settings */}
          <section className="config-section">
            <div className="settings-list">
              <div className="setting-row">
                <span className="setting-label">Withdrawal Enabled</span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={withdrawalEnabled}
                    onChange={(e) => setWithdrawalEnabled(e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>
              
              <div className="setting-row with-input">
                <div className="setting-left">
                  <span className="setting-label">Transfer Limit (USDT)</span>
                  <input
                    type="number"
                    value={transferLimit}
                    onChange={(e) => setTransferLimit(e.target.value)}
                    placeholder="No limit"
                    className="setting-input"
                  />
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={transferLimitEnabled}
                    onChange={(e) => setTransferLimitEnabled(e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </section>

          {/* Test Connection */}
          {testResult && (
            <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
              {testResult.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
              <span>{testResult.message}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button 
            className="btn-test"
            onClick={handleTestConnection}
            disabled={isTesting || !exchangeData?.hasApiKey}
          >
            {isTesting ? <Loader2 size={16} className="spinning" /> : null}
            {isTesting ? 'Testing...' : 'Test Connection'}
          </button>
          
          <div className="footer-right">
            {exchangeData?.hasApiKey && (
              <button 
                className="btn-delete"
                onClick={handleDelete}
                disabled={isSaving}
              >
                Delete
              </button>
            )}
            <button 
              className="btn-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              className="btn-save"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? <Loader2 size={16} className="spinning" /> : null}
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExchangeConfigModal;
